// Quién resolvió un ticket y cuándo, para la Analítica del equipo.
//
// Regla de negocio (2026-10-03): el crédito es de una sola persona, la que llevó el ticket de
// una etapa de trabajo a una terminal, no de todos sus propietarios. Si el pipeline tiene
// "Resuelto" y luego "Cerrado", gana quien lo resolvió: pasar de Resuelto a Cerrado es solo un
// cambio de estatus (lo hace hasta el cierre automático de 3 días). Si se reabre y se vuelve a
// resolver, cuenta la última resolución.
import type { ActividadTicket, Etapa, Priority, Ticket } from "./tickets-data";
import { DIAS_PARA_VENCER, esEtapaTerminal } from "./estado";

export type Resolucion = {
  /** auth_user_id de quien lo resolvió; null = no hay registro de quién fue. */
  autorId: string | null;
  /** Milisegundos epoch del momento de la resolución. */
  fecha: number;
  /** true = sacado de la bitácora; false = respaldo (ticket viejo sin cambio de etapa registrado). */
  registrado: boolean;
};

const RE_ETAPA = /^Etapa actualizada a "(.+)"\.?$/;
const RE_PIPELINE = /^Pipeline cambiado a "(.+)" \(etapa: (.+)\)\.?$/;

/** Etapa destino de un renglón de actividad: la columna si existe; si no, el texto (filas viejas). */
export function etapaDestinoDe(
  a: Pick<ActividadTicket, "etapaDestinoId" | "texto" | "tipo">,
  pipelineId: string,
  etapas: Etapa[],
): Etapa | undefined {
  if (a.etapaDestinoId) return etapas.find((e) => e.id === a.etapaDestinoId);
  if (a.tipo !== "cambio_estado") return undefined;
  const m = RE_ETAPA.exec(a.texto);
  if (m) {
    return (
      etapas.find((e) => e.pipelineId === pipelineId && e.nombre === m[1]) ??
      etapas.find((e) => e.nombre === m[1])
    );
  }
  const p = RE_PIPELINE.exec(a.texto);
  if (p) return etapas.find((e) => e.nombre === p[2] && e.pipelineId === pipelineId) ?? etapas.find((e) => e.nombre === p[2]);
  return undefined;
}

/** Resolución vigente del ticket, o null si hoy no está en una etapa terminal. */
export function resolucionDelTicket(
  t: Pick<Ticket, "etapaId" | "pipelineId" | "actividad" | "fechaCierre" | "fechaCreacion" | "propietarios">,
  etapas: Etapa[],
): Resolucion | null {
  const actual = etapas.find((e) => e.id === t.etapaId);
  if (!esEtapaTerminal(actual)) return null;

  let enTerminal = false;
  let ultima: Resolucion | null = null;
  for (const a of t.actividad) {
    const destino = etapaDestinoDe(a, t.pipelineId, etapas);
    if (!destino) continue;
    const terminal = esEtapaTerminal(destino);
    if (terminal && !enTerminal) {
      ultima = { autorId: a.autorId ?? null, fecha: new Date(a.fecha).getTime(), registrado: true };
    }
    enTerminal = terminal;
  }
  // La bitácora termina en una etapa terminal: esa es la resolución vigente.
  if (ultima && enTerminal) return ultima;

  // Sin registro (ticket viejo o movido desde una app sin bitácora): solo hay a quién darle el
  // crédito si tiene un único propietario.
  return {
    autorId: t.propietarios.length === 1 ? t.propietarios[0] : null,
    fecha: new Date(t.fechaCierre ?? t.actividad[t.actividad.length - 1]?.fecha ?? t.fechaCreacion).getTime(),
    registrado: false,
  };
}

/** Texto cuando el ticket está en etapa terminal pero la bitácora no dice quién lo resolvió. */
export const SIN_REGISTRO_RESOLUCION = "Sin registro de quién lo resolvió";

/**
 * Nombre de quien resolvió el ticket, para mostrarlo en lista, kanban y detalle junto a los
 * propietarios (un ticket con 7 propietarios lo resolvió uno). null = el ticket sigue abierto.
 */
export function quienResolvio(
  t: Parameters<typeof resolucionDelTicket>[0],
  etapas: Etapa[],
  nombreDe: (id: string) => string | undefined,
): string | null {
  const r = resolucionDelTicket(t, etapas);
  if (!r) return null;
  if (!r.autorId) return SIN_REGISTRO_RESOLUCION;
  return nombreDe(r.autorId) ?? "Usuario fuera del equipo";
}

/**
 * Seguimiento obligatorio: entrar a una etapa terminal desde una de trabajo exige dejar mensaje
 * o evidencia. Pasar de Resuelto a Cerrado no lo pide: ya se documentó al resolver.
 */
export function requiereSeguimiento(origen: Etapa | undefined, destino: Etapa | undefined): boolean {
  return esEtapaTerminal(destino) && !esEtapaTerminal(origen);
}

// ─── Tiempo de resolución con holgura ──────────────────────────────────────────

/**
 * Margen sobre el plazo de la prioridad antes de marcar a alguien en rojo. Entre 100 % y
 * 100 % + holgura se marca en ámbar: un ticket que tardó 6 días con plazo de 5 no es una alerta.
 */
export const HOLGURA_PLAZO = 0.25;
/** Con menos resoluciones que esto, el tiempo de una persona no se colorea: no es representativo. */
export const MIN_RESOLUCIONES_PARA_SEMAFORO = 3;

export type SemaforoTiempo = "bien" | "holgura" | "excedido" | "sin_datos";

export function mediana(valores: number[]): number | null {
  if (!valores.length) return null;
  const v = [...valores].sort((a, b) => a - b);
  const m = Math.floor(v.length / 2);
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
}

/** Cuánto del plazo de su prioridad consumió un ticket: 1 = justo en el plazo. */
export function usoDelPlazo(dias: number, prioridad: Priority): number {
  return dias / DIAS_PARA_VENCER[prioridad];
}

/**
 * Uso del plazo de un ticket ya resuelto en `fechaResolucion`: proporción del tiempo transcurrido
 * entre el inicio del reloj y su `fechaVencimiento` (1 = resuelto justo al vencer). Aproximación:
 * el front no calcula horas hábiles, así que se mide en tiempo natural entre ambas fechas (la BD ya
 * puso el vencimiento en horas hábiles). Sin fecha de vencimiento, respaldo por días naturales.
 */
export function usoDelPlazoTicket(
  t: Pick<Ticket, "fechaCreacion" | "fechaInicioAtencion" | "fechaVencimiento" | "prioridad">,
  fechaResolucion: number,
): number {
  const inicio = new Date(t.fechaInicioAtencion || t.fechaCreacion).getTime();
  if (t.fechaVencimiento) {
    const total = new Date(t.fechaVencimiento).getTime() - inicio;
    if (total > 0) return Math.max(0, fechaResolucion - inicio) / total;
  }
  return usoDelPlazo((fechaResolucion - new Date(t.fechaCreacion).getTime()) / 86_400_000, t.prioridad);
}

/**
 * Semáforo por la mediana del uso del plazo (no por el promedio de días contra un plazo fijo):
 * así un ticket de prioridad baja no se mide con el plazo de la media y un par de tickets
 * viejos no tiñen de rojo a quien más resuelve.
 */
export function semaforoTiempo(usos: number[]): SemaforoTiempo {
  if (usos.length < MIN_RESOLUCIONES_PARA_SEMAFORO) return "sin_datos";
  const m = mediana(usos) ?? 0;
  if (m <= 1) return "bien";
  if (m <= 1 + HOLGURA_PLAZO) return "holgura";
  return "excedido";
}
